import { useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { resolveUserRoleForFlow } from "@src/utils/entry-route-bootstrap";
import logger from "@src/utils/logger";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { buildCanvasPrefillApplicationPatch, INITIAL_CANVAS_TRACKING } from "./apply-prefill";
import type { CanvasTrackingState } from "./types";

/**
 * Renderless component mounted once at the app root (see browser.tsx).
 *
 * Listens for Salesforce Canvas `pacProviderMatching.event` events via
 * CanvasContext, translates them into SharedData patches, and applies them
 * to ApplicationContext.  When a new lead arrives, the existing session is
 * cleared first so the user starts fresh.
 *
 * Tracking state (lead ID, event key, version) is held in a ref — NOT in
 * ApplicationContext — so that the effect's dependency array stays minimal
 * and doesn't re-fire on unrelated context changes.
 */
export const CanvasPrefillController: FC = () => {
  const { canvasEvent, isInSalesforce } = useCanvasContext();
  const { setDataPatch, clearSession, data } = useApplicationContext();
  const trackingRef = useRef<CanvasTrackingState>({
    ...INITIAL_CANVAS_TRACKING,
  });

  useEffect(() => {
    // Derive role from Canvas context — available before data.userRole is hydrated
    const effectiveRole = resolveUserRoleForFlow(isInSalesforce, data.isReferrerFlow);
    const result = buildCanvasPrefillApplicationPatch(
      canvasEvent,
      trackingRef.current,
      effectiveRole,
    );
    if (result.status === "ignored") {
      return;
    }

    trackingRef.current = result.nextTracking;

    if (result.shouldResetSession) {
      clearSession();
    }

    setDataPatch(result.patch);

    logger.info({
      text: "Applied Canvas prefill event to ApplicationContext",
      value: {
        keys: Object.keys(result.patch),
        shouldResetSession: result.shouldResetSession,
      },
      location: "CanvasPrefillController",
    });
  }, [canvasEvent, setDataPatch, clearSession]);

  return null;
};

export default CanvasPrefillController;
